import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';



const STATUS_LINES = [
  'Executing NCCI edits and comprehensive CPT regulatory auditing...',
  'Analyzing LCD/NCD guidelines and proprietary payer adjudication logic...',
  'Synchronizing perioperative documentation for 100% clinical integrity...',
  'Optimizing component coding and strategic modifier application...',
  'Maximizing yield while eliminating clinical-to-billing conflicts...',
  'Finalizing the consolidated clinical source of truth...',
  'Deploying autonomous logic to mitigate and preempt denials...',
  'Validating medical necessity through algorithmic clinical evidence mapping...',
  'Cross-referencing Global Surgical Package rules to prevent unbundling errors...',
  'Identifying hidden co-morbidities for HCC (Hierarchical Condition Category) accuracy...',
  'Scrubbing demographic and eligibility data to eliminate front-end friction...',
  'Audit-proofing the Operative Report via natural language processing (NLP)...',
  'Quantifying RVU (Relative Value Unit) capture to ensure physician productivity alignment...',
  "Automating the 'Clean Claim' submission via direct EDI (Electronic Data Interchange) clearinghouses...",
  "Monitoring real-time 'Payer Response' latency to accelerate the revenue clock...",
  'Reconciling 835 ERA (Electronic Remittance Advice) files against expected contract rates...',
  'Triaging partial payments through automated underpayment recovery protocols...',
  'Benchmarking Net Collection Rates (NCR) against national specialty standards...',
  'Predicting future payer behavior via historical trend-analysis and machine learning...',
  'Securing the revenue cycle loop with a recursive data-feedback audit...',
];

const PHRASE_DURATION = 4.8; // total seconds per phrase

/* ─── Logo with pulsating glow ─── */
function AnimatedLogo() {
  return (
    <div className="flex flex-col items-center gap-3">
      <span className="font-bold tracking-tight text-foreground text-[3.5rem] leading-none select-none">
        LINK
      </span>
    </div>
  );
}

/* ─── Cycling Text (no setTimeout — pure Framer Motion) ─── */
function CyclingText() {
  const [index, setIndex] = useState(0);

  const advance = useCallback((definition: string) => {
    if (definition === 'exit') {
      setIndex(prev => (prev + 1) % STATUS_LINES.length);
    }
  }, []);

  return (
    <div className="h-20 sm:h-24 flex items-center justify-center max-w-[620px] mx-auto px-4">
      <AnimatePresence mode="wait" onExitComplete={() => {}}>
        <motion.p
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 1, 0] }}
          transition={{
            duration: PHRASE_DURATION,
            times: [0, 0.167, 0.833, 1], // 0.8s in, 3.2s hold, 0.8s out
            ease: 'easeInOut',
          }}
          onAnimationComplete={advance}
          className="text-[1.15rem] sm:text-[1.4rem] md:text-[1.75rem] leading-[1.3] text-center select-none text-muted-foreground"
          style={{ fontWeight: 500 }}
        >
          {STATUS_LINES[index]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

/* ─── Progress Arc ─── */
function ProgressArc() {
  return (
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
      className="mt-4 sm:mt-6"
      style={{ opacity: 0.7 }}
    >
      <svg
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
        className="w-[48px] h-[48px] sm:w-[56px] sm:h-[56px]"
      >
        <defs>
          <linearGradient id="loArcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(199 98% 39%)" />
            <stop offset="100%" stopColor="hsl(199 98% 50%)" />
          </linearGradient>
        </defs>
        <circle
          cx="28"
          cy="28"
          r="26"
          stroke="url(#loArcGrad)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="120 44"
          fill="none"
        />
      </svg>
    </motion.div>
  );
}

/* ─── Main Overlay ─── */
interface LoadingOverlayProps {
  show: boolean;
}

export default function LoadingOverlay({ show }: LoadingOverlayProps) {
  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="flex flex-col items-center gap-8 sm:gap-10"
      >
        <AnimatedLogo />
        <CyclingText />
        <ProgressArc />
      </motion.div>
    </div>
  );
}
